<template>
  <div>
    <section class="hero-slab">
      <div class="hero-slab__media" :style="{ backgroundImage: 'url(' + site.hero.background + ')' }"></div>
      <div class="hero-slab__overlay"></div>
      <div class="hero-slab__content container reveal">
        <div class="section-title">
          <p>{{ page.eyebrow }}</p>
          <h1>{{ page.title }}</h1>
        </div>
        <p>{{ page.intro }}</p>
      </div>
    </section>

    <page-trail
      :items="[
        { label: $t('common.home'), to: '/' },
        { label: $t('nav.products'), to: '/products' }
      ]"
      :back-label="$t('common.backToHome')"
      back-to="/"
    />

    <section class="section-spacing light-panel">
      <div class="container reveal">
        <figure class="products-content-image" style="background: #000;">
          <img :src="productsIntroImage" :alt="$t('productsPage.imageAlt')" loading="lazy">
        </figure>
        <div class="section-title products-section-title">
          <p>{{ $t('productsPage.naturalWoodFloorsUpper') }}</p>
          <h2>{{ $t('productsPage.naturalWoodFloors') }}</h2>
        </div>
        <div class="products-entry-grid">
          <router-link
            v-for="item in naturalItems"
            :key="item.title"
            :to="item.href"
            class="products-entry-card"
          >
            <div class="products-entry-card__image">
              <img :src="item.image" :alt="item.title" loading="lazy">
            </div>
            <h3>{{ item.title }}</h3>
          </router-link>
        </div>
      </div>
    </section>

    <section class="section-spacing dark-panel">
      <div class="container reveal">
        <div class="section-title section-title--light products-section-title">
          <p>{{ $t('productsPage.structure') }}</p>
          <h2>{{ $t('productsPage.structureTitle') }}</h2>
        </div>
        <div class="products-structure-grid">
          <article v-for="item in page.structures" :key="item.title">
            <img :src="item.image" :alt="item.title" loading="lazy">
            <div>
              <h3>{{ item.title }}</h3>
            </div>
          </article>
        </div>
      </div>
    </section>

    <section class="section-spacing light-panel">
      <div class="container reveal">
        <div class="section-title products-section-title">
          <p>{{ $t('productsPage.surfaceProcessing') }}</p>
          <h2>{{ surfaceSection ? surfaceSection.title : $t('productsPage.surface') }}</h2>
        </div>
        <div class="products-surface-grid">
          <router-link
            v-for="item in surfaceItems"
            :key="item.title"
            to="/customize"
            class="products-surface-card"
          >
            <div class="products-surface-card__image">
              <img :src="item.image" :alt="item.title" loading="lazy">
            </div>
            <h3>{{ item.title }}</h3>
          </router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import site from '../mock/strongova'
import PageTrail from '../components/PageTrail.vue'
import { fetchPublicCategories, fetchPublicPageSections, resolveAssetUrl } from '../api/public'

export default {
  name: 'ProductsPage',
  components: {
    PageTrail
  },
  data () {
    return {
      site,
      page: site.productsPage,
      backendCategories: [],
      pageSections: []
    }
  },
  computed: {
    introSection () {
      return this.pageSections.find(item => item.pageKey === 'products' && item.sectionKey === 'intro') || null
    },
    surfaceSection () {
      return this.pageSections.find(item => item.pageKey === 'products' && item.sectionKey === 'surface') || null
    },
    productsIntroImage () {
      return this.introSection ? resolveAssetUrl(this.introSection.image, this.page.contentImage) : this.page.contentImage
    },
    surfaceItems () {
      if (this.surfaceSection && Array.isArray(this.surfaceSection.imagesJson) && this.surfaceSection.imagesJson.length) {
        return this.surfaceSection.imagesJson.map(item => ({
          title: item.title,
          image: resolveAssetUrl(item.image, this.site.hero.background)
        }))
      }
      return this.page.surfaces
    },
    naturalItems () {
      if (this.backendCategories.length) {
        return this.backendCategories.map(item => {
          const match = this.page.categories.find(category => category.slug === item.slug || category.title === item.name)
          return {
            title: item.name,
            slug: item.slug,
            href: '/products-list/' + item.slug,
            image: resolveAssetUrl(item.image, match ? match.image : this.site.hero.background)
          }
        })
      }

      const group = this.page.primaryGroups.find(item => item.title === 'NATURAL WOOD FLOORS')
      if (!group) return []
      return group.children.map(child => {
        const match = this.page.categories.find(item => item.title === child.title)
        return {
          ...child,
          image: match ? match.image : this.site.hero.background
        }
      })
    }
  },
  created () {
    Promise.all([
      fetchPublicCategories({ type: 'product' }),
      fetchPublicPageSections({ pageKey: 'products' })
    ])
      .then(([categories, sections]) => {
        this.backendCategories = categories
        this.pageSections = sections
      })
      .catch(() => {
        this.backendCategories = []
        this.pageSections = []
      })
  }
}
</script>

<style>
.products-content-image {
  margin: 0 0 70px;
}

.products-content-image img {
  width: 100%;
  display: block;
  border-radius: 12px;
  box-shadow: 0 22px 44px rgba(23, 20, 17, 0.08);
}

.products-section-title {
  margin-bottom: 34px;
}

.products-entry-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 40px 24px;
}

.products-entry-card {
  display: block;
  color: inherit;
}

.products-entry-card__image {
  aspect-ratio: 4 / 3;
  overflow: hidden;
  background: #eee8df;
}

.products-entry-card__image img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  transition: transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
}

.products-entry-card:hover .products-entry-card__image img {
  transform: scale(1.06);
}

.products-entry-card h3 {
  margin: 14px 0 8px;
  padding: 0 14px;
  font-size: 22px;
  font-weight: 400;
  line-height: 1.24;
  letter-spacing: 0.02em;
}

.products-entry-card p {
  margin: 0;
  padding: 0 14px 14px;
  color: rgba(23, 20, 17, 0.68);
  font-size: 15px;
  line-height: 1.6;
}

.products-structure-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
}

.products-structure-grid article {
  display: grid;
  grid-template-columns: 170px minmax(0, 1fr);
  gap: 24px;
  align-items: center;
  min-height: 210px;
  padding: 34px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.03);
}

.products-structure-grid img {
  width: 100%;
  display: block;
}

.products-structure-grid h3 {
  margin: 0 0 10px;
  font-size: 22px;
  font-weight: 400;
}

.products-structure-grid p {
  margin: 0;
  line-height: 1.8;
  color: rgba(245, 243, 239, 0.78);
}

.products-surface-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 40px 24px;
}

.products-surface-card {
  display: block;
  color: inherit;
}

.products-surface-card__image {
  aspect-ratio: 4 / 3;
  overflow: hidden;
  background: #eee8df;
}

.products-surface-card__image img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  transition: transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
}

.products-surface-card:hover .products-surface-card__image img {
  transform: scale(1.06);
}

.products-surface-card h3 {
  margin: 14px 0 14px;
  padding: 0 14px;
  font-size: 21px;
  font-weight: 400;
  line-height: 1.24;
  letter-spacing: 0.02em;
}

@media (max-width: 1100px) {
  .products-entry-grid,
  .products-surface-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .products-structure-grid,
  .products-structure-grid article {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .products-content-image {
    margin-bottom: 40px;
  }

  .products-section-title {
    margin-bottom: 24px;
  }

  .products-entry-grid,
  .products-surface-grid {
    grid-template-columns: 1fr;
    gap: 18px;
  }

  .products-entry-card h3,
  .products-surface-card h3 {
    margin-top: 12px;
    padding: 0 10px;
    font-size: 19px;
  }

  .products-entry-card p {
    padding: 0 10px 12px;
    font-size: 14px;
  }

  .products-structure-grid {
    gap: 16px;
  }

  .products-structure-grid article {
    min-height: 0;
    padding: 22px;
    gap: 18px;
  }

  .products-structure-grid h3 {
    font-size: 20px;
  }
}
</style>
